// @flow

import _ from 'lodash';

import ArrayUtils from '../../utils/ArrayUtils';
import Utils from '../../utils/Utils';

import ApiObject from '../ApiObject';
import OrderProductOption from './OrderProductOption';
import Product from '../menu/Product';
import RestaurantMenu from '../menu/RestaurantMenu';
import ProductOption from '../menu/ProductOption';
import MenuPartProduct from '../menu/MenuPartProduct';
import CategoryItem from '../menu/CategoryItem';

export default class OrderProduct extends ApiObject {

  id: string;

  name: string;

  sequence: number;

  options: Array<OrderProductOption> = [];

  comment: string = '';

  orderId: string;

  price: number = 0;

  basePrice: number = 0;

  additionalPrice: number = 0;

  baseAdditionalPrice: number = 0;

  /**
    * Sets the quantity of the same product. Front only
    * Before sending the order, we split the OrderProduct with the quantity
    */
  quantity: number = 1;

  // used to store ui data, will be merged back with order-preview
  ui: Object = {};

  postJsonAssignation(data: Object) {
    this.options = [];
    _.each(data.options, (optionData) => {
      const orderProductOption:OrderProductOption = new OrderProductOption();
      orderProductOption.fromJson(optionData);
      this.options.push(orderProductOption);
    });
  }

  fromProduct(product: Product, mode: OrderMode) {
    this.id = product.id;
    this.name = product.name;
    this.basePrice = product.getPriceForMode(mode);
    this.price = this.basePrice;

    if (!_.isEmpty(product.cartModifiers)) {
      this.price = product.getCartModifiersPrice(mode);
    }

    return this;
  }

  fromProductAndMenuPartProduct(product: Product, menuPartProduct: MenuPartProduct, mode: OrderMode) {
    this.fromProduct(product, mode);

    this.baseAdditionalPrice = menuPartProduct.additionalPrice;
    this.additionalPrice = this.baseAdditionalPrice;

    return this;
  }

  //
  //
  //

  isValidWithRestaurantMenu(restaurantMenu: RestaurantMenu): boolean {
    const product: Product = restaurantMenu.getProductWithId(this.id);

    // const options = restaurantMenu.getProductOptionsWithIds(product.optionsIds);
    const requiredOptions = restaurantMenu.getRequiredProductOptionsWithIds(product.optionsIds);

    // verify all required options are set.
    let res = true;
    _.each(requiredOptions, (requiredProductOption: ProductOption) => {
      const option: OrderProductOption = ArrayUtils.find(this.options, { 'id': requiredProductOption.id });

      if (_.isNil(option)) {
        res = false;
        return false; // quit loop.
      } else {
        // verify values are ok.
        if (option.values.length < requiredProductOption.quantityMin) {
          res = false;
          return false; // quit loop.
        }
      }
    });

    return res;
  }

  //
  //
  //

  equals(other: OrderProduct) {
    return this.id === other.id && this.comment === other.comment && this.optionsEquals(other.options) && this.price === other.price;
  }

  optionsEquals(otherOptions: Array<OrderProductOption>): boolean {
    if (otherOptions.length !== this.options.length) {
      return false;
    }

    // TODO: Handle not same order.
    for (let i = 0; i < this.options.length; i++) {
      if (!this.options[i].equals(otherOptions[i])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Merge options of `other` Order Product on this
   */
  mergeWithOrderProduct(productInfo: Product, other: OrderProduct) {
    _.each(other.options, (otherOption: OrderProductOption) => {
      if (productInfo.haveOptionWithId(otherOption.id)) {
        // our product have the option, we copy the option to our current object
        this.options.push(otherOption.copy());
      }
    });
  }

  //
  //
  //

  getDescription(): string {
    let description = '';
    const separator = ', ';

    _.each(this.options, (option: OrderProductOption) => {
      description += option.getDescription() + separator;
    });

    // remove last separator.
    return description.substring(0, description.length - separator.length);
  }

  getTotalPrice(): number {
    return this.price + this.getTotalOptionsAdditionnalPrice();
  }

  getTotalPriceWithQuantity(): number {
    return this.getTotalPrice() * this.quantity;
  }

  getTotalAdditionnalPriceForMenu(): number {
    return this.additionalPrice + this.getTotalOptionsAdditionnalPrice();
  }

  getTotalOptionsAdditionnalPrice(): number {
    let price = 0;
    _.each(this.options, (orderProductOption: OrderProductOption) => {
      price += orderProductOption.getTotalAdditionnalPrice();
    });

    return price;
  }

  //
  //
  //

  getOptionWithId(optionId: String): ?OrderProductOption {
    return ArrayUtils.find(this.options, { 'id': optionId });
  }

  //
  //
  //

  populateOrderId() {
    this.orderId = `p${Utils.generateUID()}`;
  }

  //
  //
  //

  addOption(orderProductOption: OrderProductOption) {
    this.options.push(orderProductOption);
  }

  deleteOption(orderProductOption: OrderProductOption) {
    this.options = ArrayUtils.removeObject(this.options, { 'id': orderProductOption.id });
  }

  updateOrAddOption(orderProductOption: OrderProductOption) {
    this.options = ArrayUtils.updateObjectOrAdd(
      this.options,
      orderProductOption,
      { 'id': orderProductOption.id }
    );
  }

  updateOption(orderProductOption: OrderProductOption) {
    this.options = ArrayUtils.updateObject(
      this.options,
      orderProductOption,
      { 'id': orderProductOption.id }
    );
  }
}
